import { useAuthStore } from '@/stores/auth';

/**
 * Reads the current user's permission matrix from the auth store and provides
 * a `can(featureKey)` helper for sidebar gating.
 *
 * Permissions are scoped to the active organization (set via `setActiveOrg`).
 * If the cached user object predates Phase 3 (no `permissions` field), every
 * `can()` call returns `true` so existing sessions don't see a blank sidebar.
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);

  const orgs = user?.organizations ?? [];
  const org = orgs.find((o) => o.id === activeOrgId) ?? orgs[0] ?? null;
  const role = org?.role ?? null;
  const permissions = org?.permissions;

  function can(featureKey: string): boolean {
    // Graceful fallback: if permissions not yet in cache, show everything
    if (!Array.isArray(permissions) || !role) {
      return true;
    }
    const entry = permissions.find((p) => p.role === role && p.feature_key === featureKey);
    return entry?.allowed ?? false;
  }

  const isStaff = (user?.email ?? '').endsWith('@solidafy.com');

  return { can, isStaff, role, activeOrg: org };
}
